import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

const FLYOUT_OFFSET = 8;
const FLYOUT_MAX_HEIGHT_GAP = 16;

function isPathActive(pathname, to) {
  if (!to) return false;
  if (pathname === to) return true;
  return pathname.startsWith(`${to}/`);
}

export function hasActiveChild(pathname, children = []) {
  return children.some(
    (child) =>
      isPathActive(pathname, child.to) ||
      (Array.isArray(child.children) && hasActiveChild(pathname, child.children)),
  );
}

function CollapsedFlyout({ item, anchorRef, open, onClose }) {
  const panelRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 320 });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return undefined;

    function updatePosition() {
      const rect = anchorRef.current.getBoundingClientRect();
      const maxHeight = Math.max(160, window.innerHeight - rect.top - FLYOUT_MAX_HEIGHT_GAP);
      setPosition({
        top: rect.top,
        left: rect.right + FLYOUT_OFFSET,
        maxHeight,
      });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;

    function onKeyDown(event) {
      if (event.key === 'Escape') onClose?.();
    }

    function onPointerDown(event) {
      if (panelRef.current?.contains(event.target)) return;
      if (anchorRef.current?.contains(event.target)) return;
      onClose?.();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      aria-label={item.label}
      style={{ top: position.top, left: position.left, maxHeight: position.maxHeight }}
      className="fixed z-[60] flex min-w-[14rem] max-w-[18rem] flex-col overflow-hidden rounded-md border border-white/10 bg-[var(--sidebar)] text-[var(--sidebar-foreground)] shadow-lg"
    >
      <div className="shrink-0 border-b border-white/10 px-3 py-2 text-xs font-semibold tracking-wide text-white/80">
        {item.label}
      </div>
      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain p-1.5">
        {item.children.map((child) => (
          <SidebarNavItem
            key={child.to || child.label}
            item={child}
            collapsed={false}
            onNavigate={onClose}
          />
        ))}
      </div>
    </div>,
    document.body,
  );
}

const navItemBaseClass =
  'flex items-center rounded-md font-sans text-sm font-medium leading-5 text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-muted)]';

const navItemLabelClass = 'font-sans text-sm font-medium leading-5';

export function SidebarNavItem({ item, collapsed, onNavigate, open = false, onToggle, onCloseFlyout }) {
  const location = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const childActive = hasChildren && hasActiveChild(location.pathname, item.children);
  const buttonRef = useRef(null);
  const iconSize = collapsed ? 22 : 18;

  if (hasChildren) {
    return (
      <div>
        <button
          ref={buttonRef}
          type="button"
          className={cn(
            navItemBaseClass,
            collapsed ? 'h-10 w-10 justify-center' : 'w-full gap-3 px-3 py-2',
            childActive && 'bg-[var(--sidebar-muted)]/60 text-white',
          )}
          onClick={() => onToggle?.(item.label)}
          aria-expanded={open}
          aria-haspopup={collapsed ? 'menu' : undefined}
          title={collapsed ? item.label : undefined}
        >
          {item.icon ? <item.icon size={iconSize} aria-hidden /> : null}
          {!collapsed ? (
            <>
              <span className={cn(navItemLabelClass, 'flex-1 text-left')}>{item.label}</span>
              <ChevronDown
                size={16}
                className={cn('shrink-0 transition-transform', open && 'rotate-180')}
                aria-hidden
              />
            </>
          ) : null}
        </button>

        {collapsed ? (
          <CollapsedFlyout
            item={item}
            anchorRef={buttonRef}
            open={open}
            onClose={onCloseFlyout}
          />
        ) : null}

        {!collapsed && open ? (
          <div className="ml-3 mt-1 space-y-1 border-l border-white/10 pl-2">
            {item.children.map((child) => (
              <SidebarNavItem
                key={child.to || child.label}
                item={child}
                collapsed={false}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          navItemBaseClass,
          collapsed ? 'h-10 w-10 justify-center' : 'w-full gap-3 px-3 py-2',
          isActive && 'bg-[var(--sidebar-muted)] text-white',
        )
      }
      title={collapsed ? item.label : undefined}
    >
      {item.icon ? <item.icon size={iconSize} aria-hidden /> : null}
      {!collapsed ? <span className={navItemLabelClass}>{item.label}</span> : null}
    </NavLink>
  );
}
